import { ValueMap, isAdvancedValueMap } from '../../types';
import ValueMapVisual from './ValueMapVisual';
import { AdvancedValueMapVisual } from './AdvancedValueMapVisual';
import { GoogleSheetsSnippet } from './GoogleSheetsSnippet';

interface ValueMapPanelProps {
  valueMap: ValueMap | null;
  loading: boolean;
  error: string | null;
  onExportPDF: () => void;
  onExportPNG: () => void;
}

export default function ValueMapPanel({
  valueMap,
  loading,
  error,
  onExportPDF,
  onExportPNG,
}: ValueMapPanelProps) {
  const isAdvanced = valueMap && isAdvancedValueMap(valueMap);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-300 flex justify-between items-center bg-white">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Value Map</h2>
          <p className="text-sm text-gray-600">
            {isAdvanced ? 'Advanced Mode - 13 Fields' : 'Simple Mode - 3 Fields'}
          </p>
        </div>
        {valueMap && (
          <div className="flex gap-3">
            <button
              onClick={onExportPDF}
              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Export PDF
            </button>
            <button
              onClick={onExportPNG}
              className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Export PNG
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        {loading && !valueMap && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-cloudflare-orange mx-auto mb-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Generating your value map...</p>
            </div>
          </div>
        )}

        {!loading && !valueMap && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-400">
              <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-semibold">No value map yet</p>
              <p className="text-sm mt-2">Paste an Executive Summary to get started</p>
            </div>
          </div>
        )}

        {valueMap && (
          <>
            {isAdvanced ? (
              <AdvancedValueMapVisual valueMap={valueMap as any} />
            ) : (
              <ValueMapVisual valueMap={valueMap as any} />
            )}
            {/* Google Sheets Integration shown for both modes */}
            <GoogleSheetsSnippet valueMap={valueMap} />
          </>
        )}
      </div>
    </div>
  );
}
